import { DataStore } from '../stores';
import { FILTER_SEARCH_KEYS } from '../constants';
import { action, computed, observable, toJS } from 'mobx';
import get from 'lodash/get';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';
import { FilterSearchKeys } from '../models/components/Filter';
import { isNullOrUndefined } from 'util';
import { ClearIndividualFilters } from '../models/configuration/filter/ClearIndividualFilters';

export class FilterStore extends BaseStore {
    dataStore: DataStore;
    @observable filtersOpen: boolean = false;

    constructor(stores: Stores) {
        super(stores);
        this.dataStore = stores.data;
    }

    @computed
    get filters() {
        const filters = this.dataStore.config.filtersConfig.map(filter => {
            return {
                ...filter,
                values: get(this.dataStore.filters, filter.property, []),
                filterSearchKey: get(FILTER_SEARCH_KEYS, filter.type, ''),
            };
        });

        return filters;
    }

    @action
    toggleFilters = () => {
        this.filtersOpen = !this.filtersOpen;
    };

    @computed
    get filtersCount(): number {
        return [
            ...this.dataStore.filterSearch,
            ...this.dataStore.documentFilterSearch,
            ...this.dataStore.documentPropertySearch,
        ].length;
    }

    @computed
    get filtersActive(): boolean {
        return this.filtersCount > 0 ? true : false;
    }

    @computed
    get clearFiltersLabel(): string {
        let label = this.dataStore.config.clearFiltersLabel;
        if (isNullOrUndefined(label)) {
            label = 'Clear filters';
        }
        return label;
    }

    @computed
    get clearIndividualFilters(): ClearIndividualFilters {
        return (
            this.dataStore.config.clearIndividualFilters || {
                enabled: false,
                align: 'left',
            }
        );
    }

    @action
    clearFilters = () => this.dataStore.clearFilters();

    @action
    clearFiltersOfType = (
        property: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        this.dataStore.clearFiltersOfType(property, filterSearchKey);
    };

    @action
    toggleFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        checked: boolean
    ) => {
        if (checked) {
            this.removeFilter(property, value, filterSearchKey);
        } else {
            this.addFilter(property, value, filterSearchKey);
        }
    };

    @action
    addFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        const existingPropertyIndex = updatedSearch.findIndex(
            search => search.property === property
        );

        if (existingPropertyIndex >= 0) {
            updatedSearch[existingPropertyIndex].values.push(value);
            this.dataStore[filterSearchKey] = updatedSearch;
        } else {
            updatedSearch.push({
                property,
                values: [value],
                matchtype: 'MATCH',
            });
            this.dataStore[filterSearchKey] = updatedSearch;
        }
    };

    @action
    removeFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        const existingPropertyIndex = updatedSearch.findIndex(
            search => search.property === property
        );

        if (existingPropertyIndex >= 0) {
            updatedSearch[existingPropertyIndex].values = updatedSearch[
                existingPropertyIndex
            ].values.filter(existingValue => existingValue !== value);

            if (updatedSearch[existingPropertyIndex].values.length > 0) {
                this.dataStore[filterSearchKey] = updatedSearch;
            } else {
                this.dataStore[filterSearchKey] = updatedSearch.filter(
                    search => search.property !== property
                );
            }
        } else {
            this.dataStore[filterSearchKey] = updatedSearch.filter(
                search => search.property !== property
            );
        }
    };

    isChecked = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ): boolean => {
        const existingProperty = this.dataStore[filterSearchKey].find(
            search => search.property === property
        );

        if (existingProperty) {
            return existingProperty.values.includes(value);
        }

        return false;
    };

    isActive = (property: string, value: string): boolean => {
        const filterKeys = Object.keys(this.dataStore.activeFilters);

        if (filterKeys.includes(property)) {
            return this.dataStore.activeFilters[property]
                .map(filterValue => filterValue.value)
                .includes(value);
        }

        return true;
    };
}



// WEBPACK FOOTER //
// ./src/stores/FilterStore.ts